import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAstemasterQueue } from '../lib/api';

/**
 * Widget topbar che mostra lo stato della coda Astemaster (visibile globalmente).
 * - Polla ogni 8s
 * - Mostra pill compatto "⏳ N · ~M min" quando c'è almeno un job (running o queued)
 * - Click → drawer con barra progresso job in esecuzione + lista coda con ETA
 */
export default function AstemasterQueueWidget() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const tick = () => fetchAstemasterQueue().then((d) => { if (alive) setData(d); }).catch(() => {});
    tick();
    const t = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Chiudi popover al click fuori
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const running = data?.running;
  const queued = data?.queued || [];
  const total = (running ? 1 : 0) + queued.length;
  if (!total) return null;

  const avg = data?.avg_duration_sec || 600;
  const elapsed = running?.elapsed_seconds || 0;
  const pct = Math.min(99, Math.round((elapsed / avg) * 100));
  const remainingRunning = Math.max(0, avg - elapsed);
  const etaTotalSec = remainingRunning + (queued.length * avg);

  return (
    <div ref={popoverRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={pillStyle}
              title={`${total} analisi in corso/coda · ETA ${fmtDuration(etaTotalSec)}`}>
        <span style={spinStyle} />
        <span>{total} {total === 1 ? 'analisi' : 'analisi'} · ~{fmtDuration(etaTotalSec)}</span>
      </button>

      {open && (
        <div style={popoverStyle}>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 8 }}>
            Media storica: {fmtDuration(avg)} per analisi
          </div>

          {running ? (
            <div style={section}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>In esecuzione</div>
              <Link to={`/app/immobili/${running.immobile_id}`} style={addrLink} onClick={() => setOpen(false)}>
                {running.indirizzo}{running.comune ? `, ${running.comune}` : ''}
              </Link>
              <div style={progressOuter}>
                <div style={{ ...progressInner, width: `${pct}%` }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 4 }}>
                {fmtDuration(elapsed)} / ~{fmtDuration(avg)} · {pct}%
              </div>
            </div>
          ) : (
            <div style={{ ...section, color: 'var(--fg-subtle)', fontSize: 12 }}>
              Worker fermo · prossimo in partenza
            </div>
          )}

          {queued.length > 0 && (
            <div style={section}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>In coda ({queued.length})</div>
              {queued.map((q, idx) => {
                const eta = remainingRunning + (idx + 1) * avg;
                return (
                  <div key={q.id} style={queueRow}>
                    <span style={{ width: 18, color: 'var(--fg-subtle)', fontSize: 11 }}>{idx + 1}.</span>
                    <Link to={`/app/immobili/${q.immobile_id}`} style={{ ...addrLink, flex: 1 }} onClick={() => setOpen(false)}>
                      {q.indirizzo}{q.comune ? `, ${q.comune}` : ''}
                    </Link>
                    <span style={{ fontSize: 11, color: 'var(--fg-subtle)', whiteSpace: 'nowrap' }}>
                      ~{fmtDuration(eta)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function fmtDuration(sec) {
  if (!sec || sec < 0) return '—';
  const m = Math.round(sec / 60);
  if (m < 1) return '<1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m` : `${h}h`;
}

const pillStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '5px 10px', borderRadius: 999,
  border: '1px solid oklch(0.85 0.05 240)',
  background: 'oklch(0.97 0.03 240)', color: 'oklch(0.35 0.13 240)',
  fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
};
const spinStyle = {
  display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
  border: '2px solid oklch(0.85 0.13 240)', borderTopColor: 'transparent',
  animation: 'asteq-spin 1s linear infinite',
};
const popoverStyle = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1000,
  background: 'white', border: '1px solid oklch(0.85 0 0)', borderRadius: 10,
  padding: 12, boxShadow: '0 8px 24px oklch(0 0 0 / 0.15)',
  minWidth: 320, maxWidth: 'min(420px, 92vw)',
};
const section = { padding: '8px 0', borderTop: '1px solid oklch(0.94 0 0)' };
const addrLink = {
  fontSize: 13, color: 'oklch(0.45 0.13 250)', textDecoration: 'none',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
};
const progressOuter = {
  height: 6, background: 'oklch(0.93 0 0)', borderRadius: 999, overflow: 'hidden', marginTop: 6,
};
const progressInner = {
  height: '100%', background: 'oklch(0.65 0.18 240)', borderRadius: 999,
  transition: 'width 0.5s ease',
};
const queueRow = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
};

// CSS keyframes per lo spinner — iniettato una volta
if (typeof document !== 'undefined' && !document.getElementById('asteq-spin-css')) {
  const s = document.createElement('style');
  s.id = 'asteq-spin-css';
  s.textContent = '@keyframes asteq-spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(s);
}
